import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { 
  BookOpen, 
  Plus, 
  Search,
  Edit2,
  Trash2,
  X,
  Filter,
  Tag,
  Globe,
  FileText,
  ExternalLink,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const CATEGORY_INFO = {
  epr_regulations: { name: "EPR Regulations", icon: FileText, color: "bg-blue-500/15 text-blue-400" },
  pro_organizations: { name: "PRO Organizations", icon: BookOpen, color: "bg-emerald-500/15 text-emerald-500" },
  country_specifics: { name: "Country Specifics", icon: Globe, color: "bg-violet-500/15 text-violet-500" },
  fee_structures: { name: "Fee Structures", icon: Tag, color: "bg-amber-500/15 text-amber-500" },
  reporting_deadlines: { name: "Reporting Deadlines", icon: Calendar, color: "bg-red-500/15 text-red-400" },
  compliance_penalties: { name: "Compliance Penalties", icon: FileText, color: "bg-rose-500/15 text-rose-400" },
  waste_streams: { name: "Waste Streams", icon: RefreshCw, color: "bg-teal-500/15 text-teal-400" },
  custom: { name: "Custom", icon: Sparkles, color: "bg-white/10 text-gray-600" }
};

const WASTE_STREAMS = ["packaging", "weee", "batteries", "furniture", "textiles", "tyres"];

export default function KnowledgeBase() {
  const api = useApi();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [countries, setCountries] = useState([]);
  const [expandedEntries, setExpandedEntries] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "custom",
    country_code: "",
    waste_stream: "",
    tags: "",
    source_url: "",
    effective_date: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, countryFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (countryFilter !== "all") params.append("country_code", countryFilter);
      
      const [entriesRes, statsRes, countriesRes] = await Promise.all([
        api.get(`/api/knowledge-base/?${params.toString()}`),
        api.get("/api/knowledge-base/stats"),
        api.get("/api/countries/")
      ]);
      
      setEntries(entriesRes.data?.entries || []);
      setStats(statsRes.data);
      setCountries(countriesRes.data?.countries || []);
    } catch (error) {
      console.error("Error fetching knowledge base:", error);
      toast.error("Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchData();
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/api/knowledge-base/search?q=${encodeURIComponent(searchQuery)}`);
      setEntries(res.data?.entries || []);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }

    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        country_code: formData.country_code || null,
        waste_stream: formData.waste_stream || null,
        source_url: formData.source_url || null,
        effective_date: formData.effective_date || null
      };

      if (editingEntry) {
        await api.put(`/api/knowledge-base/${editingEntry.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/api/knowledge-base/", payload);
        toast.success("Entry created");
      }
      
      setShowDialog(false);
      setEditingEntry(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error("Failed to save entry");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this knowledge base entry?")) return;
    
    try {
      await api.delete(`/api/knowledge-base/${id}`);
      toast.success("Entry deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const openEditDialog = (entry) => {
    setFormData({
      title: entry.title,
      content: entry.content,
      category: entry.category,
      country_code: entry.country_code || "",
      waste_stream: entry.waste_stream || "",
      tags: (entry.tags || []).join(", "),
      source_url: entry.source_url || "",
      effective_date: entry.effective_date || ""
    });
    setEditingEntry(entry);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      category: "custom",
      country_code: "",
      waste_stream: "",
      tags: "",
      source_url: "",
      effective_date: ""
    });
  };

  const toggleExpanded = (id) => {
    setExpandedEntries(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="knowledge-base-page">
      <div className="page-header compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Knowledge Base</h1>
            <p className="page-subtitle">
              Manage EPR regulations, PROs, and compliance data for AI enrichment
            </p>
          </div>
          <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-entry-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
          <div className="card p-2 text-center">
            <div className="text-lg font-bold text-violet-500">{stats.total}</div>
            <div className="text-[10px] text-gray-500">Total Entries</div>
          </div>
          {Object.entries(stats.by_category || {}).slice(0, 7).map(([cat, count]) => (
            <div key={cat} className="card p-2 text-center">
              <div className="text-lg font-bold text-gray-900">{count}</div>
              <div className="text-[10px] text-gray-500 truncate">{CATEGORY_INFO[cat]?.name || cat}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-8 h-8 text-sm bg-gray-50 border-gray-200 text-gray-900"
              data-testid="search-input"
            />
          </div>
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="category-filter">
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_INFO).map(([id, info]) => (
              <SelectItem key={id} value={id}>{info.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="country-filter">
            <Globe className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {countries.map(c => (
              <SelectItem key={c.country_code} value={c.country_code}>
                {c.flag_emoji} {c.country_code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" className="h-8" onClick={handleSearch}>
          <Search className="w-3.5 h-3.5 mr-1" />
          Search
        </Button>
      </div>

      {/* Entries List */}
      {entries.length === 0 ? (
        <div className="card p-8 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">No Knowledge Base Entries</h3>
          <p className="text-xs text-gray-500 mb-4">
            Add entries to enhance AI enrichment accuracy
          </p>
          <Button size="sm" onClick={() => setShowDialog(true)} className="bg-violet-500 hover:bg-violet-600">
            <Plus className="w-4 h-4 mr-1.5" />
            Add First Entry
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(entry => {
            const catInfo = CATEGORY_INFO[entry.category] || CATEGORY_INFO.custom;
            const isExpanded = expandedEntries[entry.id];
            
            return (
              <Collapsible
                key={entry.id}
                open={isExpanded}
                onOpenChange={() => toggleExpanded(entry.id)}
              >
                <div className="card p-3" data-testid={`kb-entry-${entry.id}`}>
                  <div className="flex items-start gap-3">
                    <CollapsibleTrigger asChild>
                      <button className="mt-0.5 text-gray-400 hover:text-gray-600">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </CollapsibleTrigger>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{entry.title}</h4>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {entry.content.substring(0, 150)}...
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          <Badge className={`text-[10px] ${catInfo.color}`}>
                            {catInfo.name}
                          </Badge>
                          {entry.country_code && (
                            <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">
                              {entry.country_code}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                        {entry.waste_stream && (
                          <span className="flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            {entry.waste_stream}
                          </span>
                        )}
                        {entry.tags?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {entry.tags.slice(0, 3).join(", ")}
                          </span>
                        )}
                        {entry.source_url && (
                          <a href={entry.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-violet-500 hover:underline">
                            <ExternalLink className="w-3 h-3" />
                            Source
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-6 w-6 p-0 text-gray-500 hover:text-gray-900 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }}
                        data-testid={`edit-entry-${entry.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                        data-testid={`delete-entry-${entry.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  
                  <CollapsibleContent>
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                        {entry.content}
                      </div>
                      {entry.effective_date && (
                        <div className="mt-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Effective: {entry.effective_date}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? "Edit Entry" : "Add Knowledge Base Entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="E.g., Germany Packaging EPR Requirements"
                data-testid="entry-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content" className="text-gray-600">Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Enter detailed information about the regulation, PRO, fee structure, etc..."
                rows={6}
                data-testid="entry-content-input"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger data-testid="entry-category-select" className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {Object.entries(CATEGORY_INFO).map(([id, info]) => (
                      <SelectItem key={id} value={id}>{info.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-600">Country</Label>
                <Select value={formData.country_code} onValueChange={(v) => setFormData({...formData, country_code: v})}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="none">None (General)</SelectItem>
                    {countries.map(c => (
                      <SelectItem key={c.country_code} value={c.country_code}>
                        {c.flag_emoji} {c.country_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Waste Stream</Label>
                <Select value={formData.waste_stream} onValueChange={(v) => setFormData({...formData, waste_stream: v})}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue placeholder="Select waste stream" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="none">None (General)</SelectItem>
                    {WASTE_STREAMS.map(ws => (
                      <SelectItem key={ws} value={ws}>{ws.charAt(0).toUpperCase() + ws.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="effective_date" className="text-gray-600">Effective Date</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({...formData, effective_date: e.target.value})}
                  className="bg-gray-50 border-gray-200 text-gray-900"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags" className="text-gray-600">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="e.g., registration, fees, deadline"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="source_url" className="text-gray-600">Source URL</Label>
              <Input
                id="source_url"
                value={formData.source_url}
                onChange={(e) => setFormData({...formData, source_url: e.target.value})}
                placeholder="https://..."
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-entry-btn" className="bg-violet-500 hover:bg-violet-600">
              {editingEntry ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
